/**
 * @module i18n/fr
 * @description French translations
 */

import type { Translations } from './types';

export const fr: Translations = {
  common: {
    appName: 'refero',
    loading: 'Chargement...',
    error: 'Erreur',
    success: 'Succès',
    warning: 'Avertissement',
    info: 'Info',
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    edit: 'Modifier',
    create: 'Créer',
    creating: 'Création',
    close: 'Fermer',
    done: 'Terminé',
    confirm: 'Confirmer',
    yes: 'Oui',
    no: 'Non',
    ok: 'OK',
    back: 'Retour',
    next: 'Suivant',
    search: 'Rechercher',
    clear: 'Effacer',
    refresh: 'Actualiser',
    copy: 'Copier',
    duplicate: 'Dupliquer',
    export: 'Exporter',
    import: 'Importer',
    settings: 'Paramètres',
    help: 'Aide',
    logout: 'Déconnexion',
    noResults: 'Aucun résultat trouvé',
    required: 'Obligatoire',
    optional: 'Facultatif',
    retry: 'Réessayer',
  },

  auth: {
    title: 'Authentification',
    apiKeyLabel: 'Clé API Zotero',
    apiKeyPlaceholder: 'Entrez votre clé API Zotero',
    apiKeyHelp:
      'Vous pouvez trouver votre clé API dans les paramètres Zotero sur zotero.org/settings/keys',
    apiKeyInvalid: 'Clé API invalide',
    login: 'Connexion',
    loggingIn: 'Connexion en cours...',
    loginSuccess: 'Connexion réussie',
    loginFailed: 'Échec de la connexion',
    logoutSuccess: 'Déconnexion réussie',
    selectLibrary: 'Sélectionner une bibliothèque',
    selectLibraryHelp: 'Choisissez la bibliothèque avec laquelle vous souhaitez travailler',
    myLibrary: 'Ma bibliothèque',
    groupLibraries: 'Bibliothèques de groupe',
    noGroups: 'Aucune bibliothèque de groupe disponible',
  },

  library: {
    title: 'Bibliothèque',
    myLibrary: 'Ma bibliothèque',
    groups: 'Groupes',
    noGroups: 'Aucun groupe',
    groupLibraries: 'Bibliothèques partagées',
    selectLibrary: 'Sélectionner une bibliothèque',
    librarySelected: 'Bibliothèque sélectionnée',
    loadingLibraries: 'Chargement des bibliothèques...',
    loadingGroups: 'Chargement des groupes...',
    errorLoadingGroups: 'Échec du chargement des groupes',
  },

  collection: {
    title: 'Collection',
    collections: 'Collections',
    allItems: 'Tous les documents',
    unfiled: 'Documents non classés',
    trash: 'Corbeille',
    duplicates: 'Documents en double',
    publications: 'Mes publications',
    newCollection: 'Nouvelle collection',
    newSubcollection: 'Nouvelle sous-collection',
    renameCollection: 'Renommer la collection',
    deleteCollection: 'Supprimer la collection',
    collectionName: 'Nom de la collection',
    collectionNamePlaceholder: 'Entrez le nom de la collection',
    collectionCreated: 'Collection créée',
    collectionRenamed: 'Collection renommée',
    collectionDeleted: 'Collection supprimée',
    collectionEmpty: 'Cette collection est vide',
    confirmDelete: 'Supprimer la collection ?',
    confirmDeleteMessage:
      'Êtes-vous sûr de vouloir supprimer cette collection ? Les documents ne seront pas supprimés.',
    expandAll: 'Tout développer',
    collapseAll: 'Tout réduire',
    noCollections: 'Aucune collection',
    loadingCollections: 'Chargement des collections...',
    errorLoadingCollections: 'Échec du chargement des collections',
    emptyTrash: 'Vider la corbeille',
    emptyTrashConfirm:
      'Êtes-vous sûr de vouloir supprimer définitivement tous les documents de la corbeille ?',
    trashEmptied: 'Corbeille vidée',
  },

  item: {
    title: 'Document',
    items: 'Documents',
    item: 'document',
    newItem: 'Nouveau document',
    selectType: 'Sélectionner le type de document',
    editItem: 'Modifier le document',
    deleteItem: 'Supprimer le document',
    deleteItems: 'Supprimer les documents',
    duplicateItem: 'Dupliquer le document',
    itemCreated: 'Document créé',
    itemUpdated: 'Document mis à jour',
    itemDeleted: 'Document déplacé vers la corbeille',
    itemsDeleted: 'Documents déplacés vers la corbeille',
    itemDuplicated: 'Document dupliqué',
    confirmDelete: 'Supprimer le document ?',
    confirmDeleteMessage: 'Êtes-vous sûr de vouloir déplacer ce document vers la corbeille ?',
    confirmDeleteMultiple:
      'Êtes-vous sûr de vouloir déplacer {{count}} documents vers la corbeille ?',
    noItems: 'Aucun document dans cette collection',
    loadingItems: 'Chargement des documents...',
    errorLoadingItems: 'Échec du chargement des documents',
    // Item fields (basic)
    itemType: 'Type de document',
    title_field: 'Titre',
    creators: 'Auteurs',
    date: 'Date',
    dateAdded: "Date d'ajout",
    dateModified: 'Date de modification',
    abstract: 'Résumé',
    publisher: 'Éditeur',
    publicationTitle: 'Publication',
    volume: 'Volume',
    issue: 'Numéro',
    pages: 'Pages',
    doi: 'DOI',
    isbn: 'ISBN',
    issn: 'ISSN',
    url: 'URL',
    accessDate: 'Date de consultation',
    language: 'Langue',
    tags: 'Marqueurs',
    notes: 'Notes',
    attachments: 'Pièces jointes',
    // Creator management
    addCreator: 'Ajouter un auteur',
    removeCreator: 'Supprimer',
    firstName: 'Prénom',
    lastName: 'Nom',
    // All field labels (from Zotero documentation)
    fields: {
      // Champs généraux
      title: 'Titre',
      creators: 'Auteurs',
      date: 'Date',
      abstractNote: 'Résumé',
      language: 'Langue',
      shortTitle: 'Titre abrégé',
      url: 'URL',
      accessDate: 'Consulté',
      rights: 'Droits',
      extra: 'Extra',
      // Livres et périodiques
      publicationTitle: 'Publication',
      bookTitle: 'Titre du livre',
      dictionaryTitle: 'Titre du dictionnaire',
      encyclopediaTitle: "Titre de l'encyclopédie",
      volume: 'Volume',
      issue: 'Numéro',
      pages: 'Pages',
      edition: 'Édition',
      series: 'Collection',
      seriesNumber: 'N° de collection',
      seriesTitle: 'Titre de la série',
      seriesText: 'Texte de la série',
      numberOfVolumes: 'Nbre de volumes',
      numPages: 'Nbre de pages',
      section: 'Section',
      place: 'Lieu',
      publisher: "Maison d'édition",
      journalAbbreviation: 'Abrév. de revue',
      ISBN: 'ISBN',
      ISSN: 'ISSN',
      DOI: 'DOI',
      // Archives et accès
      archive: 'Archive',
      archiveLocation: "Loc. dans l'archive",
      libraryCatalog: 'Catalogue de bibl.',
      callNumber: 'Cote',
      // Rapports et thèses
      reportType: 'Type',
      reportNumber: 'N° de rapport',
      institution: 'Institution',
      university: 'Université',
      thesisType: 'Type',
      // Présentations et conférences
      proceedingsTitle: 'Titre des actes',
      conferenceName: 'Nom de la conférence',
      meetingName: 'Nom de la rencontre',
      presentationType: 'Type',
      // Enregistrements et diffusions
      videoRecordingFormat: 'Format',
      audioRecordingFormat: 'Format',
      audioFileType: 'Type de fichier',
      runningTime: 'Durée',
      programTitle: 'Titre du programme',
      episodeNumber: "N° d'épisode",
      network: 'Chaîne',
      label: 'Label',
      distributor: 'Distributeur',
      studio: 'Studio',
      genre: 'Genre',
      // Images, œuvres et cartes
      artworkMedium: 'Support',
      artworkSize: 'Dimensions',
      mapType: 'Type',
      scale: 'Échelle',
      // Sources primaires
      medium: 'Support',
      interviewMedium: 'Support',
      manuscriptType: 'Type',
      letterType: 'Type',
      subject: 'Sujet',
      // Sites web
      websiteTitle: 'Titre du site web',
      websiteType: 'Type de site web',
      blogTitle: 'Titre du blog',
      forumTitle: 'Titre du forum/liste',
      postType: 'Type de message',
      // Logiciels
      versionNumber: 'Version',
      system: 'Système',
      company: 'Société',
      programmingLanguage: 'Langage',
      // Jeux de données
      identifier: 'Identifiant',
      repository: 'Dépôt',
      repositoryLocation: 'Loc. du dépôt',
      format: 'Format',
      citationKey: 'Clé de citation',
      // Normes
      number: 'Numéro',
      organization: 'Organisation',
      committee: 'Comité',
      type: 'Type',
      status: 'Statut',
      // Juridique: Législation
      nameOfAct: 'Nom de la loi',
      billNumber: 'N° de projet de loi',
      code: 'Code',
      codeVolume: 'Volume du code',
      codeNumber: 'N° du code',
      publicLawNumber: 'N° de loi publique',
      dateEnacted: 'Date de promulgation',
      codePages: 'Pages du code',
      legislativeBody: 'Organe législatif',
      session: 'Session',
      history: 'Historique',
      documentNumber: 'N° de document',
      // Juridique: Affaires
      caseName: "Nom de l'affaire",
      court: 'Tribunal',
      dateDecided: 'Date de décision',
      docketNumber: 'N° de dossier',
      reporter: 'Recueil',
      reporterVolume: 'Volume du recueil',
      firstPage: 'Première page',
      // Juridique: Brevets
      country: 'Pays',
      assignee: 'Cessionnaire',
      issuingAuthority: 'Autorité de délivrance',
      patentNumber: 'N° de brevet',
      filingDate: 'Date de dépôt',
      issueDate: 'Date de délivrance',
      applicationNumber: 'N° de demande',
      priorityNumbers: 'N° de priorité',
      references: 'Références',
      legalStatus: 'Statut juridique',
      // Extra/CSL
      originalDate: 'Date originale',
      originalTitle: 'Titre original',
      originalPublisher: 'Éditeur original',
      originalPublisherPlace: 'Lieu éditeur original',
      PMID: 'PMID',
      PMCID: 'PMCID',
    },
    // Item types
    types: {
      book: 'Livre',
      bookSection: 'Chapitre de livre',
      journalArticle: 'Article de revue',
      magazineArticle: 'Article de magazine',
      newspaperArticle: 'Article de journal',
      thesis: 'Thèse',
      report: 'Rapport',
      conferencePaper: 'Communication de conférence',
      webpage: 'Page web',
      document: 'Document',
      presentation: 'Présentation',
      patent: 'Brevet',
      statute: 'Loi',
      case: 'Affaire juridique',
      interview: 'Interview',
      film: 'Film',
      artwork: "Œuvre d'art",
      podcast: 'Podcast',
      videoRecording: 'Enregistrement vidéo',
      audioRecording: 'Enregistrement audio',
      encyclopediaArticle: "Article d'encyclopédie",
      dictionaryEntry: 'Entrée de dictionnaire',
      manuscript: 'Manuscrit',
      radioBroadcast: 'Émission radio',
      tvBroadcast: 'Émission TV',
      blogPost: 'Billet de blog',
      forumPost: 'Message de forum',
      letter: 'Lettre',
      email: 'Courriel',
      instantMessage: 'Message instantané',
      map: 'Carte',
      dataset: 'Jeu de données',
      standard: 'Norme',
      computerProgram: 'Logiciel',
      bill: 'Projet de loi',
      hearing: 'Audition',
    },
    // Creator types
    creatorTypes: {
      author: 'Auteur',
      editor: 'Éditeur scientifique',
      translator: 'Traducteur',
      contributor: 'Contributeur',
      bookAuthor: 'Auteur du livre',
      seriesEditor: 'Directeur de collection',
      reviewedAuthor: 'Auteur critiqué',
      director: 'Réalisateur',
      artist: 'Artiste',
      performer: 'Interprète',
      composer: 'Compositeur',
      interviewer: 'Intervieweur',
      interviewee: 'Interviewé',
      producer: 'Producteur',
      presenter: 'Présentateur',
      scriptwriter: 'Scénariste',
      wordsBy: 'Paroles de',
      castMember: 'Membre de la distribution',
      guest: 'Invité',
      podcaster: 'Podcasteur',
      cartographer: 'Cartographe',
      programmer: 'Programmeur',
      recipient: 'Destinataire',
      commenter: 'Commentateur',
      sponsor: 'Parrain',
      cosponsor: 'Co-parrain',
      counsel: 'Conseiller juridique',
      inventor: 'Inventeur',
      attorneyAgent: 'Avocat/Agent',
    },
  },

  search: {
    placeholder: 'Rechercher des documents...',
    searching: 'Recherche en cours...',
    noResults: 'Aucun document ne correspond à votre recherche',
    resultsCount: '{{count}} résultats trouvés',
    recentSearches: 'Recherches récentes',
    clearHistory: "Effacer l'historique",
    searchIn: 'Rechercher dans',
    searchAll: 'Tous les champs',
    searchTitles: 'Titres',
    searchCreators: 'Auteurs',
    searchTags: 'Marqueurs',
  },

  sort: {
    sortBy: 'Trier par',
    ascending: 'Croissant',
    descending: 'Décroissant',
    title: 'Titre',
    creator: 'Auteur',
    date: 'Date',
    dateAdded: "Date d'ajout",
    dateModified: 'Date de modification',
    itemType: 'Type de document',
    year: 'Année',
    publisher: 'Éditeur',
  },

  pagination: {
    page: 'Page',
    of: 'sur',
    items: 'documents',
    showing: 'Affichage de',
    first: 'Première',
    previous: 'Précédente',
    next: 'Suivante',
    last: 'Dernière',
    perPage: 'par page',
  },

  export: {
    title: 'Exporter',
    format: 'Format',
    bibtex: 'BibTeX',
    biblatex: 'BibLaTeX',
    ris: 'RIS',
    csljson: 'CSL JSON',
    mods: 'MODS',
    refer: 'Refer/BibIX',
    tei: 'TEI',
    wikipedia: 'Wikipedia',
    csv: 'CSV',
    exportSuccess: 'Export réussi',
    exportFailed: "Échec de l'export",
    copyToClipboard: 'Copier dans le presse-papiers',
    copiedToClipboard: 'Copié dans le presse-papiers',
    copyFailed: 'Échec de la copie',
    copyTitle: 'Copier le titre',
    copyUrl: "Copier l'URL",
    copyCitation: 'Copier la citation',
    // New keys for export modal
    exportItems: 'Exporter les références',
    exportItem: 'Exporter la référence',
    exportCollection: 'Exporter la collection',
    selectedItems: '{{count}} référence(s) sélectionnée(s)',
    formatLabel: "Format d'export",
    styleLabel: 'Style de citation',
    bibliography: 'Bibliographie formatée',
    download: 'Télécharger',
    preview: 'Aperçu',
    generating: 'Génération en cours...',
    noItemsSelected: 'Aucune référence sélectionnée',
    selectItemsFirst: "Veuillez d'abord sélectionner des références à exporter",
  },

  detailsPanel: {
    title: 'Détails',
    noItemSelected: 'Aucun document sélectionné',
    selectItemToView: 'Sélectionnez un document pour afficher ses détails',
    viewMode: 'Afficher',
    editMode: 'Modifier',
    saveChanges: 'Enregistrer les modifications',
    discardChanges: 'Annuler les modifications',
    unsavedChanges: 'Modifications non enregistrées',
    confirmDiscard: 'Annuler les modifications non enregistrées ?',
    info: 'Info',
    notes: 'Notes',
    tags: 'Marqueurs',
    related: 'Connexe',
  },

  errors: {
    generic: "Une erreur s'est produite",
    network: 'Erreur réseau. Veuillez vérifier votre connexion.',
    unauthorized: 'Non autorisé. Veuillez vous reconnecter.',
    forbidden: "Vous n'avez pas la permission d'effectuer cette action.",
    notFound: "La ressource demandée n'a pas été trouvée.",
    serverError: 'Erreur serveur. Veuillez réessayer plus tard.',
    timeout: "Délai d'attente dépassé. Veuillez réessayer.",
    rateLimited: 'Trop de requêtes. Veuillez patienter et réessayer.',
    invalidApiKey: 'Clé API invalide. Veuillez vérifier vos identifiants.',
    loadFailed: 'Échec du chargement des données',
    saveFailed: "Échec de l'enregistrement des modifications",
    deleteFailed: 'Échec de la suppression',
    connectionLost: 'Connexion perdue',
    tryAgain: 'Réessayer',
  },

  settings: {
    title: 'Paramètres',
    language: 'Langue',
    theme: 'Thème',
    darkMode: 'Mode sombre',
    lightMode: 'Mode clair',
    autoTheme: 'Système par défaut',
    panelSizes: 'Taille des panneaux',
    resetPanels: 'Réinitialiser la taille des panneaux',
    clearCache: 'Vider le cache',
    clearCacheConfirm: 'Êtes-vous sûr de vouloir vider le cache ?',
    cacheCleared: 'Cache vidé',
    about: 'À propos',
    version: 'Version',
    // Préférences types d'items et champs
    itemTypes: 'Types de documents',
    fields: 'Champs',
    itemTypesDescription:
      'Sélectionnez les types de documents que vous souhaitez utiliser. Les types désactivés n\'apparaîtront pas dans le menu "Nouveau document".',
    fieldsDescription:
      "Choisissez les champs à afficher pour chaque type de document. Cela simplifie le formulaire d'édition.",
    selectItemType: 'Configurer les champs pour',
    selectAll: 'Tout sélectionner',
    selectCommon: 'Types courants',
    showAllFields: 'Tous les champs',
    showEssential: 'Essentiels uniquement',
    resetToDefault: 'Réinitialiser',
    resetAll: 'Réinitialiser toutes les préférences',
    confirmReset: 'Êtes-vous sûr de vouloir réinitialiser toutes les préférences ?',
    preferencesReset: 'Préférences réinitialisées',
  },

  a11y: {
    skipToContent: 'Aller au contenu',
    mainNavigation: 'Navigation principale',
    collectionsPanel: 'Panneau des collections',
    itemsPanel: 'Panneau des documents',
    detailsPanel: 'Panneau des détails',
    closeModal: 'Fermer la fenêtre modale',
    expandCollection: 'Développer la collection',
    collapseCollection: 'Réduire la collection',
    selectItem: 'Sélectionner le document',
    openMenu: 'Ouvrir le menu',
    closeMenu: 'Fermer le menu',
    loading: 'Chargement',
    sortAscending: 'Tri croissant',
    sortDescending: 'Tri décroissant',
    itemActions: 'Actions sur le document',
    collectionActions: 'Actions sur la collection',
  },

  scan: {
    title: 'Scanner un ISBN',
    scanButton: 'Scanner',
    scanning: 'Scan en cours...',
    hint: 'Pointez votre appareil photo vers un code-barres ISBN',
    scannedISBN: 'ISBN scanné',
    scanAnother: 'Scanner un autre',
    cameraAccessDenied: "Accès à la caméra refusé. Veuillez autoriser l'accès pour scanner.",
    noCameraFound: 'Aucune caméra trouvée sur cet appareil.',
    unsupportedBrowser:
      "L'API BarcodeDetector n'est pas supportée par ce navigateur. Utilisez Chrome ou Edge.",
    // ISBN search
    searchingCatalogs: 'Recherche dans les catalogues...',
    foundIn: 'Trouvé dans',
    notFound: 'Aucune notice trouvée dans les catalogues',
    searchedIn: 'Recherché dans',
  },

  import: {
    // General
    title: 'Importer des références',
    importButton: 'Importer',
    // Tabs
    tabCatalogs: 'Catalogues bibliographiques',
    tabFile: 'Importer un fichier',
    // Catalog search
    searchPlaceholder: 'Titre, auteur, ISBN...',
    searchIndex: 'Index de recherche',
    documentType: 'Type de document',
    searching: 'Recherche en cours...',
    noResults: 'Aucun résultat trouvé',
    tryDifferentSearch: "Essayez avec d'autres termes de recherche",
    welcomeMessage: 'Recherchez des notices à importer',
    searchHint: 'Entrez un titre, un auteur, un ISBN ou un sujet',
    enterSearchTerm: 'Veuillez saisir un terme de recherche',
    searchFailed: 'La recherche a échoué',
    resultsCount: '{{count}} résultat(s) trouvé(s)',
    selectAll: 'Tout sélectionner',
    untitled: 'Sans titre',
    selectRecordsFirst: "Veuillez d'abord sélectionner des notices à importer",
    noLibrarySelected: 'Aucune bibliothèque sélectionnée',
    importing: 'Import en cours...',
    importSelected: 'Importer ({{count}})',
    importSuccess: '{{count}} notice(s) importée(s) avec succès',
    importPartialFailed: "{{count}} notice(s) n'ont pas pu être importée(s)",
    // Source selection
    source: 'Source',
    sourceSudoc: 'Sudoc (catalogues français)',
    sourceBnf: 'BnF',
    sourceGoogleBooks: 'Google Books',
    sourceSwisscovery: 'Swisscovery',
    sourceOpenLibrary: 'Open Library',
    sourceHarvardLibrary: 'Harvard Library',
    sourceWikidata: 'Wikidata',
    sudocSearchHint: 'Rechercher dans Sudoc (catalogues universitaires)',
    googleBooksSearchHint: 'Rechercher dans Google Books',
    harvardLibrarySearchHint: 'Rechercher dans Harvard Library',
    bnfSearchHint: 'Rechercher dans BnF (Bibliothèque nationale de France)',
    swisscoverySearchHint: 'Rechercher dans Swisscovery (bibliothèques suisses)',
    openLibrarySearchHint: 'Rechercher dans Open Library (Internet Archive)',
    wikidataSearchHint: 'Rechercher dans Wikidata (base de connaissances libre)',
    addCriteria: 'Ajouter un critère',
    // File import
    dropZoneText: 'Glissez-déposez un fichier ici',
    dropZoneHint: 'ou cliquez pour sélectionner un fichier',
    browseFiles: 'Parcourir...',
    supportedFormats: 'Formats supportés',
    detectedFormat: 'Format détecté',
    fileParseSuccess: '{{count}} référence(s) trouvée(s) dans le fichier',
    fileParseFailed: 'Impossible de lire le fichier',
    recordsFound: '{{count}} référence(s) trouvée(s)',
  },
};

export default fr;
